<template>
  <div class="map-container">
    <div ref="mapContainer" class="three-container"></div>
    
    <div class="controls-overlay">
      <div class="control-group">
        <label>WMTS Layer:</label>
        <select v-model="selectedLayerIndex" @change="changeLayer">
          <option v-for="(layer, index) in wmtsLayers" :key="index" :value="index">
            {{ layer.name }}
          </option>
        </select>
      </div>
      
      <div class="control-group">
        <label>Zoom Level:</label>
        <input 
          type="range" 
          v-model.number="zoomLevel" 
          :min="zoomBounds.min" 
          :max="zoomBounds.max" 
          step="1"
          @input="changeZoomLevel"
        />
        <span>{{ zoomLevel }}</span>
      </div>
    </div>
    
    <div class="info-panel">
      <p>Loaded tiles: {{ loadedTileCount }}</p>
      <p>Position: Dam Square, Amsterdam</p>
      <p>Zoom: {{ zoomLevel }}</p>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'Map',
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      loadedTileCount: 0,
      tileGroup: null,
      
      // Dam Square coordinates in RD (EPSG:28992)
      rdx: 121393,
      rdy: 487334,
      tileMatrixMetaCache: {},
      isLoadingTiles: false,
      isSyncingCameraHeight: false,
      currentTileMatrixSet: null,
      pendingZoomLevel: null,
      frustumSize: 1200,
      baseCameraHeight: 1000,
      cameraHeightRange: {
        min: 400,
        max: 4000
      },
      mouseController: null,
      zoomAnimation: null,
      zoomAnimationQueue: [],
      zoomAnimationDuration: 320,
      zoomGestureTimeout: null,
      zoomBounds: {
        min: 10,
        max: 16
      },
      
      selectedLayerIndex: 0,
      zoomLevel: 14,
      
      wmtsLayers: [
        {
          name: "PDOK Luchtfoto",
          url: "https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0",
          layer: "Actueel_ortho25",
          tileMatrixSet: "EPSG:28992"
        },
        {
          name: "BRT Achtergrondkaart",
          url: "https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0",
          layer: "standaard",
          tileMatrixSet: "EPSG:28992"
        },
        {
          name: "BRT Achtergrondkaart (Grijs)",
          url: "https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0",
          layer: "grijs",
          tileMatrixSet: "EPSG:28992"
        }
      ]
    };
  },
  
  mounted() {
    this.initThreeJS();
    this.loadTiles();
  },
  
  beforeDestroy() {
    if (this.tileGroup) {
      this.scene.remove(this.tileGroup);
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
    this.disposeMouseController();
    if (this.zoomGestureTimeout) {
      clearTimeout(this.zoomGestureTimeout);
      this.zoomGestureTimeout = null;
    }
    this.zoomAnimation = null;
    this.zoomAnimationQueue = [];
    window.removeEventListener('resize', this.onWindowResize);
  },
  
  methods: {
    initThreeJS() {
      // Scene setup
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x87CEEB); // Sky blue background
      
      // Camera setup (orthographic top-down)
      const aspect = window.innerWidth / window.innerHeight;
      const halfFrustumHeight = this.frustumSize / 2;
      const halfFrustumWidth = halfFrustumHeight * aspect;
      this.camera = new THREE.OrthographicCamera(
        -halfFrustumWidth,
        halfFrustumWidth,
        halfFrustumHeight,
        -halfFrustumHeight,
        0.1,
        5000
      );
      this.camera.position.set(0, this.baseCameraHeight, 0); // Kijk van bovenaf naar beneden
      this.camera.zoom = 1;
      this.camera.lookAt(0, 0, 0);
      this.camera.up.set(0, 0, -1);
      this.updateCameraFrustumForHeight();
      
      // Renderer setup
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      
      this.$refs.mapContainer.appendChild(this.renderer.domElement);
      
      // Mouse controls setup
      this.initMouseController();
      
      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      this.scene.add(ambientLight);
      
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(100, 100, 50);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      this.scene.add(directionalLight);
      
      // Event listeners
      window.addEventListener('resize', this.onWindowResize);
      
      // Start render loop
      this.animate();
    },

    initMouseController() {
      if (!this.renderer || !this.renderer.domElement) {
        return;
      }
      this.disposeMouseController();
      const element = this.renderer.domElement;
      const handleWheel = (event) => {
        event.preventDefault();
        this.handleWheelZoom(event);
      };
      element.addEventListener('wheel', handleWheel, { passive: false });
      this.mouseController = {
        element,
        handleWheel
      };
    },

    disposeMouseController() {
      if (!this.mouseController) {
        return;
      }
      const { element, handleWheel } = this.mouseController;
      if (element && handleWheel) {
        element.removeEventListener('wheel', handleWheel);
      }
      this.mouseController = null;
    },

    updateCameraFrustumForHeight() {
      if (!this.camera) {
        return;
      }
      const aspect = window.innerWidth / window.innerHeight || 1;
      const heightScale = this.camera.position.y / this.baseCameraHeight;
      const effectiveFrustum = this.frustumSize * heightScale;
      const halfFrustumHeight = effectiveFrustum / 2;
      const halfFrustumWidth = halfFrustumHeight * aspect;
      this.camera.left = -halfFrustumWidth;
      this.camera.right = halfFrustumWidth;
      this.camera.top = halfFrustumHeight;
      this.camera.bottom = -halfFrustumHeight;
      this.camera.updateProjectionMatrix();
    },

    handleWheelZoom(event) {
      if (!this.camera) {
        return;
      }
      const delta = event.deltaY;
      if (delta === 0 || !this.currentTileMatrixSet) {
        return;
      }
      // Natural scrolling: delta > 0 when swiping fingers downward (zoom in)
      const direction = delta > 0 ? 1 : -1;
      if (direction === 0) {
        return;
      }
      this.queueZoomStep(direction);
      this.refreshZoomGestureTimeout();
    },

    queueZoomStep(direction) {
      const tileMatrixSet = this.currentTileMatrixSet;
      if (!tileMatrixSet || !Array.isArray(tileMatrixSet.levels) || !tileMatrixSet.levels.length) {
        return;
      }
      const baseLevel = this.getReferenceLevelForQueue();
      const nextLevel = this.getAdjacentLevel(baseLevel, direction);
      if (nextLevel == null) {
        return;
      }
      const lastQueued = this.zoomAnimationQueue.length
        ? this.zoomAnimationQueue[this.zoomAnimationQueue.length - 1]
        : (this.zoomAnimation ? this.zoomAnimation.targetLevel : null);
      if (lastQueued === nextLevel) {
        return;
      }
      this.zoomAnimationQueue.push(nextLevel);
      if (!this.zoomAnimation) {
        this.startNextZoomAnimation();
      }
    },

    refreshZoomGestureTimeout() {
      if (this.zoomGestureTimeout) {
        clearTimeout(this.zoomGestureTimeout);
      }
      this.zoomGestureTimeout = setTimeout(() => {
        this.zoomGestureTimeout = null;
        this.zoomAnimationQueue = [];
      }, 180);
    },

    getReferenceLevelForQueue() {
      if (this.zoomAnimationQueue.length) {
        return this.zoomAnimationQueue[this.zoomAnimationQueue.length - 1];
      }
      if (this.zoomAnimation) {
        return this.zoomAnimation.targetLevel;
      }
      if (this.camera && this.currentTileMatrixSet) {
        const height = this.camera.position.y;
        const nearest = this.findNearestLevelForHeight(height);
        if (nearest != null) {
          return nearest;
        }
      }
      return this.zoomLevel;
    },

    getAdjacentLevel(currentLevel, direction) {
      const tileMatrixSet = this.currentTileMatrixSet;
      if (!tileMatrixSet) {
        return null;
      }
      const order = tileMatrixSet.levelsByHeight?.length
        ? tileMatrixSet.levelsByHeight
        : tileMatrixSet.levels;
      if (!Array.isArray(order) || !order.length) {
        return null;
      }
      let index = order.indexOf(currentLevel);
      if (index === -1) {
        index = this.getLevelIndexForValue(currentLevel, order, tileMatrixSet.heightTargets);
      }
      if (direction > 0) {
        return index > 0 ? order[index - 1] : null;
      }
      return index < order.length - 1 ? order[index + 1] : null;
    },

    getLevelIndexForValue(value, order, heightTargets) {
      if (!Array.isArray(order) || !order.length) {
        return 0;
      }
      let closestIndex = 0;
      let bestScore = Infinity;
      const valueHeight = heightTargets && Number.isFinite(heightTargets[value])
        ? heightTargets[value]
        : null;
      order.forEach((level, idx) => {
        const targetHeight = heightTargets && Number.isFinite(heightTargets[level])
          ? heightTargets[level]
          : null;
        let score;
        if (valueHeight !== null && targetHeight !== null) {
          score = Math.abs(targetHeight - valueHeight);
        } else {
          score = Math.abs(level - value);
        }
        if (score < bestScore) {
          bestScore = score;
          closestIndex = idx;
        }
      });
      return closestIndex;
    },

    findNearestLevelForHeight(height) {
      const tileMatrixSet = this.currentTileMatrixSet;
      if (!tileMatrixSet || !tileMatrixSet.heightTargets) {
        return null;
      }
      const order = tileMatrixSet.levelsByHeight?.length
        ? tileMatrixSet.levelsByHeight
        : tileMatrixSet.levels;
      if (!Array.isArray(order) || !order.length) {
        return null;
      }
      let bestLevel = order[0];
      let bestScore = Infinity;
      order.forEach((level) => {
        const targetHeight = tileMatrixSet.heightTargets[level];
        if (!Number.isFinite(targetHeight)) {
          return;
        }
        const score = Math.abs(targetHeight - height);
        if (score < bestScore) {
          bestScore = score;
          bestLevel = level;
        }
      });
      return bestLevel;
    },

    startNextZoomAnimation() {
      if (!this.zoomAnimationQueue.length || !this.currentTileMatrixSet) {
        return;
      }
      const nextLevel = this.zoomAnimationQueue.shift();
      const targetHeight = this.currentTileMatrixSet.heightTargets?.[nextLevel];
      if (!Number.isFinite(targetHeight)) {
        this.startNextZoomAnimation();
        return;
      }
      const currentlyLoading = this.isLoadingTiles;
      this.zoomLevel = nextLevel;
      if (currentlyLoading) {
        this.pendingZoomLevel = nextLevel;
      } else {
        this.pendingZoomLevel = null;
        this.loadTiles();
      }

      const currentHeight = this.camera.position.y;
      if (!Number.isFinite(currentHeight) || Math.abs(currentHeight - targetHeight) < 0.5) {
        this.camera.position.y = targetHeight;
        this.updateCameraFrustumForHeight();
        this.zoomAnimation = null;
        if (this.zoomAnimationQueue.length) {
          this.startNextZoomAnimation();
        }
        return;
      }

      const now = performance.now();
      this.zoomAnimation = {
        startHeight: currentHeight,
        targetHeight,
        targetLevel: nextLevel,
        startTime: now,
        duration: this.zoomAnimationDuration
      };
    },

    updateZoomAnimation(now) {
      if (!this.zoomAnimation) {
        return;
      }
      const anim = this.zoomAnimation;
      const elapsed = now - anim.startTime;
      const t = Math.min(1, elapsed / anim.duration);
      const eased = t;
      const newHeight = anim.startHeight + (anim.targetHeight - anim.startHeight) * eased;
      this.camera.position.y = newHeight;
      this.updateCameraFrustumForHeight();
      if (t >= 1) {
        this.completeZoomAnimation();
      }
    },

    completeZoomAnimation() {
      if (!this.zoomAnimation) {
        return;
      }
      this.camera.position.y = this.zoomAnimation.targetHeight;
      this.updateCameraFrustumForHeight();
      this.zoomAnimation = null;
      if (this.zoomAnimationQueue.length) {
        this.startNextZoomAnimation();
      }
    },

    async loadTiles() {
      if (this.isLoadingTiles) {
        return;
      }
      
      this.isLoadingTiles = true;
      const selectedLayer = this.wmtsLayers[this.selectedLayerIndex];
      let tileMatrixId = `${selectedLayer.tileMatrixSet}:${this.zoomLevel}`;
      
      try {
        const tileMatrixSet = await this.ensureTileMatrixSet(selectedLayer);
        if (!tileMatrixSet) {
          console.warn('Geen TileMatrixSet gevonden voor laag', selectedLayer);
          return;
        }
        this.currentTileMatrixSet = tileMatrixSet;
        this.clampCameraHeight();
        const { matrices, levels, identifierByLevel } = tileMatrixSet;

        if (levels && levels.length) {
          this.zoomBounds.min = levels[0];
          this.zoomBounds.max = levels[levels.length - 1];
        }

        let effectiveMatrixId = tileMatrixId;
        let tileMatrix = matrices[tileMatrixId];
        if (!tileMatrix) {
          const fallbackLevel = this.findFallbackZoomLevel(levels, this.zoomLevel);
          if (fallbackLevel == null) {
            console.warn(`Geen bruikbaar zoomniveau gevonden voor ${tileMatrixId}`);
            return;
          }

          const fallbackId = identifierByLevel[fallbackLevel];
          if (!fallbackId || !matrices[fallbackId]) {
            console.warn(`Fallback zoomniveau ${fallbackLevel} ontbreekt in TileMatrixSet`);
            return;
          }

          effectiveMatrixId = fallbackId;
          tileMatrix = matrices[fallbackId];
          if (this.zoomLevel !== fallbackLevel) {
            this.zoomLevel = fallbackLevel;
          }
          console.warn(`Zoomniveau ${tileMatrixId} niet beschikbaar voor deze laag, gebruik ${fallbackId} in plaats daarvan`);
        }
        tileMatrixId = effectiveMatrixId;
        const activeLevel = this.extractZoomLevel(tileMatrixId);
        if (activeLevel !== null) {
          this.applyCameraHeightForLevel(tileMatrixSet, activeLevel);
        }
        
        // Remove existing tiles
        if (this.tileGroup) {
          this.scene.remove(this.tileGroup);
        }
        
        this.tileGroup = new THREE.Group();
        this.scene.add(this.tileGroup);
        this.loadedTileCount = 0;
        
        const tileContext = this.calculateTileContext(tileMatrix);
        
        console.log(`Center tile: col ${tileContext.tileX}, row ${tileContext.tileY}, matrix ${tileMatrixId}`);
        console.log(`Tile size (meters): ${tileMatrix.tileSizeMetersX} x ${tileMatrix.tileSizeMetersY}`);
        
        const gridSize = 3;
        const offset = Math.floor(gridSize / 2);
        
        for (let x = -offset; x <= offset; x++) {
          for (let y = -offset; y <= offset; y++) {
            const tileCol = tileContext.tileX + x;
            const tileRow = tileContext.tileY + y;
            
            if (
              tileCol >= 0 && tileCol < tileMatrix.matrixWidth &&
              tileRow >= 0 && tileRow < tileMatrix.matrixHeight
            ) {
              this.loadTile(
                selectedLayer,
                tileMatrixId,
                tileCol,
                tileRow,
                x,
                y,
                tileMatrix,
                tileContext
              );
            }
          }
        }
      } catch (error) {
        console.error('Fout bij het laden van WMTS-tiles:', error);
      } finally {
        this.isLoadingTiles = false;
        if (this.pendingZoomLevel != null) {
          const targetLevel = this.pendingZoomLevel;
          this.pendingZoomLevel = null;
          if (targetLevel !== this.zoomLevel) {
            this.zoomLevel = targetLevel;
          }
          this.loadTiles();
        }
      }
    },
    
    loadTile(layer, tileMatrixId, tileX, tileY, gridX, gridY, tileMatrix, tileContext) {
      // Correct PDOK WMTS URL format
      const tileUrl = `${layer.url}?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0` +
        `&LAYER=${layer.layer}&STYLE=default&FORMAT=image%2Fpng` +
        `&TILEMATRIXSET=${encodeURIComponent(layer.tileMatrixSet)}` +
        `&TILEMATRIX=${encodeURIComponent(tileMatrixId)}` +
        `&TILEROW=${tileY}&TILECOL=${tileX}`;
      
      console.log(`Loading tile (col ${tileX}, row ${tileY}):`, tileUrl);
      
      const textureLoader = new THREE.TextureLoader();
      textureLoader.crossOrigin = 'anonymous';
      
      textureLoader.load(
        tileUrl,
        (texture) => {
          if (texture.colorSpace) {
            texture.colorSpace = THREE.SRGBColorSpace;
          }
          
          // Create tile geometry
          const geometry = new THREE.PlaneGeometry(tileMatrix.tileSizeMetersX, tileMatrix.tileSizeMetersY);
          const material = new THREE.MeshBasicMaterial({ 
            map: texture,
            side: THREE.DoubleSide
          });
          
          const tileMesh = new THREE.Mesh(geometry, material);
          
          // Position the tile in 3D space
          tileMesh.position.x = (gridX * tileMatrix.tileSizeMetersX) + (tileMatrix.tileSizeMetersX / 2) - tileContext.offsetWithinTileX;
          tileMesh.position.z = (gridY * tileMatrix.tileSizeMetersY) + (tileMatrix.tileSizeMetersY / 2) - tileContext.offsetWithinTileY;
          tileMesh.position.y = 0;
          tileMesh.rotation.x = -Math.PI / 2; // Lay flat
          
          this.tileGroup.add(tileMesh);
          this.loadedTileCount++;
          
          // Voeg een rode rand toe om de tile te markeren
          const edges = new THREE.EdgesGeometry(geometry);
          const lineMaterial = new THREE.LineBasicMaterial({ color: 0xff0000 });
          const wireframe = new THREE.LineSegments(edges, lineMaterial);
          wireframe.position.copy(tileMesh.position);
          wireframe.rotation.copy(tileMesh.rotation);
          this.tileGroup.add(wireframe);
          
          console.log(`Loaded tile col ${tileX}, row ${tileY} at grid ${gridX},${gridY}`);
        },
        (progress) => {
          console.log('Loading progress:', progress);
        },
        (error) => {
          console.error('Error loading tile:', tileUrl, error);
        }
      );
    },
    
    async changeLayer() {
      this.pendingZoomLevel = null;
      await this.loadTiles();
    },
    
    async changeZoomLevel() {
      this.pendingZoomLevel = null;
      await this.loadTiles(); // Reload with new zoom level
    },
    
    onWindowResize() {
      this.updateCameraFrustumForHeight();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    },
    
    animate(timestamp) {
      requestAnimationFrame(this.animate);
      const time = typeof timestamp === 'number' ? timestamp : performance.now();
      this.updateZoomAnimation(time);
      
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },
    
    async ensureTileMatrixSet(layer) {
      const cacheKey = `${layer.url}|${layer.tileMatrixSet}`;
      if (this.tileMatrixMetaCache[cacheKey]) {
        const cached = this.tileMatrixMetaCache[cacheKey];
        if (cached.matrices) {
          return cached;
        }
        const normalizedCached = this.normalizeTileMatrixSet(cached);
        this.tileMatrixMetaCache[cacheKey] = normalizedCached;
        return normalizedCached;
      }
      
      const capabilitiesUrl = `${layer.url}?SERVICE=WMTS&REQUEST=GetCapabilities`;
      
      const response = await fetch(capabilitiesUrl);
      if (!response.ok) {
        throw new Error(`GetCapabilities request failed (${response.status})`);
      }
      
      const xmlString = await response.text();
      const parser = new DOMParser();
      const xml = parser.parseFromString(xmlString, 'application/xml');
      
      const tileMatrixSet = this.parseTileMatrixSet(xml, layer.tileMatrixSet);
      if (!tileMatrixSet) {
        return null;
      }
      
      const normalized = this.normalizeTileMatrixSet(tileMatrixSet);
      this.tileMatrixMetaCache[cacheKey] = normalized;
      return normalized;
    },
    
    parseTileMatrixSet(xmlDocument, targetIdentifier) {
      const tileMatrixSetNodes = Array.from(xmlDocument.getElementsByTagNameNS('*', 'TileMatrixSet'));
      const targetSet = tileMatrixSetNodes.find((node) => {
        const identifierNode = Array.from(node.children).find((child) => child.localName === 'Identifier');
        return identifierNode && identifierNode.textContent.trim() === targetIdentifier;
      });
      
      if (!targetSet) {
        return null;
      }
      
      const tileMatrices = {};
      const levels = [];
      const identifierByLevel = {};
      let tileMatrixNodes = Array.from(targetSet.getElementsByTagNameNS('*', 'TileMatrix'));
      if (!tileMatrixNodes.length) {
        tileMatrixNodes = Array.from(targetSet.getElementsByTagName('TileMatrix'));
      }
      const pixelSize = 0.00028;
      
      tileMatrixNodes.forEach((matrixNode) => {
        const identifierNode = Array.from(matrixNode.children).find((child) => child.localName === 'Identifier');
        if (!identifierNode) {
          return;
        }
        
        const identifier = identifierNode.textContent.trim();
        const scaleDenominatorNode = Array.from(matrixNode.children).find((child) => child.localName === 'ScaleDenominator');
        const topLeftNode = Array.from(matrixNode.children).find((child) => child.localName === 'TopLeftCorner');
        const tileWidthNode = Array.from(matrixNode.children).find((child) => child.localName === 'TileWidth');
        const tileHeightNode = Array.from(matrixNode.children).find((child) => child.localName === 'TileHeight');
        const matrixWidthNode = Array.from(matrixNode.children).find((child) => child.localName === 'MatrixWidth');
        const matrixHeightNode = Array.from(matrixNode.children).find((child) => child.localName === 'MatrixHeight');
        
        if (!scaleDenominatorNode || !topLeftNode || !tileWidthNode || !tileHeightNode || !matrixWidthNode || !matrixHeightNode) {
          return;
        }
        
        const scaleDenominator = parseFloat(scaleDenominatorNode.textContent);
        const resolution = scaleDenominator * pixelSize;
        const topLeftCorner = topLeftNode.textContent.trim().split(/\s+/).map(Number);
        
        const tileWidth = parseInt(tileWidthNode.textContent, 10);
        const tileHeight = parseInt(tileHeightNode.textContent, 10);
        const matrixWidth = parseInt(matrixWidthNode.textContent, 10);
        const matrixHeight = parseInt(matrixHeightNode.textContent, 10);
        
        tileMatrices[identifier] = {
          identifier,
          scaleDenominator,
          resolution,
          topLeftCorner,
          tileWidth,
          tileHeight,
          matrixWidth,
          matrixHeight,
          tileSizeMetersX: tileWidth * resolution,
          tileSizeMetersY: tileHeight * resolution
        };

        const zoomLevel = this.extractZoomLevel(identifier);
        if (zoomLevel !== null && identifierByLevel[zoomLevel] === undefined) {
          identifierByLevel[zoomLevel] = identifier;
          levels.push(zoomLevel);
        }
      });
      
      levels.sort((a, b) => a - b);

      const { heightTargets, baseResolution, levelsByHeight } = this.buildCameraHeightMapping(levels, identifierByLevel, tileMatrices);
      
      return {
        matrices: tileMatrices,
        levels,
        identifierByLevel,
        heightTargets,
        levelsByHeight,
        baseResolution
      };
    },

    normalizeTileMatrixSet(rawSet) {
      if (!rawSet) {
        return null;
      }

      const matrices = rawSet.matrices ? rawSet.matrices : {};
      const identifierByLevel = rawSet.identifierByLevel ? { ...rawSet.identifierByLevel } : {};
      const levelSet = new Set();

      if (Array.isArray(rawSet.levels)) {
        rawSet.levels.forEach((level) => {
          if (Number.isFinite(level)) {
            levelSet.add(level);
          }
        });
      }

      const sourceEntries = rawSet.matrices ? Object.entries(rawSet.matrices) : Object.entries(rawSet);
      sourceEntries.forEach(([identifier, matrix]) => {
        if (!matrix || typeof matrix !== 'object') {
          return;
        }
        matrices[identifier] = matrix;
        const zoom = this.extractZoomLevel(identifier);
        if (zoom !== null) {
          levelSet.add(zoom);
          if (identifierByLevel[zoom] === undefined) {
            identifierByLevel[zoom] = identifier;
          }
        }
      });

      const levels = Array.from(levelSet).sort((a, b) => a - b);

      const { heightTargets, baseResolution, levelsByHeight } = this.buildCameraHeightMapping(levels, identifierByLevel, matrices);

      return {
        matrices,
        levels,
        identifierByLevel,
        heightTargets,
        levelsByHeight,
        baseResolution
      };
    },
    
    buildCameraHeightMapping(levels, identifierByLevel, matrices) {
      if (!Array.isArray(levels) || !levels.length) {
        return { heightTargets: {}, baseResolution: null };
      }

      let baseResolution = null;
      const rawFactors = {};

      levels.forEach((level) => {
        const identifier = identifierByLevel[level];
        const matrix = identifier ? matrices[identifier] : null;
        if (!matrix || !Number.isFinite(matrix.resolution) || matrix.resolution <= 0) {
          return;
        }
        if (baseResolution === null || matrix.resolution > baseResolution) {
          baseResolution = matrix.resolution;
        }
      });

      if (baseResolution === null) {
        return { heightTargets: {}, baseResolution: null };
      }

      const validRawFactors = [];
      levels.forEach((level) => {
        const identifier = identifierByLevel[level];
        const matrix = identifier ? matrices[identifier] : null;
        if (!matrix || !Number.isFinite(matrix.resolution) || matrix.resolution <= 0) {
          return;
        }
        const rawFactor = baseResolution / matrix.resolution;
        if (Number.isFinite(rawFactor) && rawFactor > 0) {
          rawFactors[level] = rawFactor;
          validRawFactors.push(rawFactor);
        }
      });

      if (!validRawFactors.length) {
        return { heightTargets: {}, baseResolution };
      }

      const minRaw = Math.min(...validRawFactors);
      const maxRaw = Math.max(...validRawFactors);
      const minHeight = this.cameraHeightRange.min;
      const maxHeight = this.cameraHeightRange.max;
      const midHeight = (minHeight + maxHeight) / 2;

      const logMin = Math.log(minRaw);
      const range = Math.log(maxRaw) - logMin;

      const heightTargets = {};
      levels.forEach((level) => {
        const rawFactor = rawFactors[level];
        if (!Number.isFinite(rawFactor) || rawFactor <= 0) {
          return;
        }
        let normalized;
        if (!Number.isFinite(range) || range === 0) {
          normalized = midHeight;
        } else {
          const t = (Math.log(rawFactor) - logMin) / range;
          normalized = maxHeight - t * (maxHeight - minHeight);
        }
        heightTargets[level] = normalized;
      });

      const levelsByHeight = Array.from(levels).sort((a, b) => {
        const heightA = Number.isFinite(heightTargets[a]) ? heightTargets[a] : Infinity;
        const heightB = Number.isFinite(heightTargets[b]) ? heightTargets[b] : Infinity;
        if (heightA === heightB) {
          return a - b;
        }
        return heightA - heightB;
      });

      return {
        heightTargets,
        levelsByHeight,
        baseResolution
      };
    },
    
    calculateTileContext(tileMatrix) {
      const [originX, originY] = tileMatrix.topLeftCorner;
      const tileSizeX = tileMatrix.tileSizeMetersX;
      const tileSizeY = tileMatrix.tileSizeMetersY;
      
      const offsetX = this.rdx - originX;
      const offsetY = originY - this.rdy;
      
      const tileOffsetX = offsetX / tileSizeX;
      const tileOffsetY = offsetY / tileSizeY;
      
      const tileX = Math.floor(tileOffsetX);
      const tileY = Math.floor(tileOffsetY);
      
      return {
        tileX,
        tileY,
        offsetWithinTileX: (tileOffsetX - tileX) * tileSizeX,
        offsetWithinTileY: (tileOffsetY - tileY) * tileSizeY
      };
    },
    
    findFallbackZoomLevel(levels, desiredLevel) {
      if (!Array.isArray(levels) || !levels.length) {
        return null;
      }
      
      let fallback = null;
      for (let i = levels.length - 1; i >= 0; i--) {
        if (levels[i] <= desiredLevel) {
          fallback = levels[i];
          break;
        }
      }
      
      if (fallback === null) {
        fallback = levels[levels.length - 1];
      }
      
      return fallback;
    },
    
    extractZoomLevel(identifier) {
      if (!identifier) {
        return null;
      }
      const parts = identifier.split(':');
      const last = parts[parts.length - 1];
      const parsed = Number(last);
      return Number.isFinite(parsed) ? parsed : null;
    },

    clampCameraHeight() {
      if (!this.camera) {
        return;
      }
      const min = this.cameraHeightRange.min;
      const max = this.cameraHeightRange.max;
      const current = this.camera.position.y;
      const clamped = THREE.MathUtils.clamp(current, min, max);
      if (clamped !== current) {
        this.isSyncingCameraHeight = true;
        this.camera.position.y = clamped;
        this.updateCameraFrustumForHeight();
        this.$nextTick(() => {
          this.isSyncingCameraHeight = false;
        });
      } else {
        this.updateCameraFrustumForHeight();
      }
    },

    applyCameraHeightForLevel(tileMatrixSet, level) {
      if (!this.camera || !tileMatrixSet || !tileMatrixSet.heightTargets) {
        return;
      }
      const target = tileMatrixSet.heightTargets[level];
      if (!Number.isFinite(target)) {
        return;
      }
      const min = this.cameraHeightRange.min;
      const max = this.cameraHeightRange.max;
      const clamped = THREE.MathUtils.clamp(target, min, max);
      if (this.zoomAnimation) {
        if (this.zoomAnimation.targetLevel === level) {
          this.zoomAnimation.targetHeight = clamped;
          return;
        }
        this.zoomAnimation = null;
        this.zoomAnimationQueue = [];
      }
      this.isSyncingCameraHeight = true;
      this.camera.position.y = clamped;
      this.updateCameraFrustumForHeight();
      this.$nextTick(() => {
        this.isSyncingCameraHeight = false;
      });
    }
    
  }
};
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.three-container {
  width: 100%;
  height: 100%;
}

.controls-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.9);
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 200px;
}

.control-group {
  margin-bottom: 10px;
}

.control-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 12px;
}

.control-group select,
.control-group input[type="range"] {
  width: 100%;
  padding: 4px;
}

.control-group input[type="checkbox"] {
  margin-right: 8px;
}

.control-group button {
  width: 100%;
  padding: 8px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.control-group button:hover {
  background: #0056b3;
}

.info-panel {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
}

.info-panel p {
  margin: 2px 0;
}
</style>
